import { useState } from 'react';
import { X, Save } from 'lucide-react';
import { maquetaService } from '../../services/api';

/**
 * PlantillaModal — MVP simplificado.
 *
 * Permite:
 *   - Ver plantillas existentes con opción de eliminar.
 *   - Guardar la página actual como una plantilla nueva (nombre + descripción),
 *     capturando los bloques con sus posiciones/tamaños (sin contenido).
 *
 * Un editor más completo (crear plantilla desde cero sin partir de una
 * página) se puede añadir después. Este flujo cubre el 80% de casos:
 * "diseñé una página bonita, quiero reutilizarla en otras ediciones".
 */
export default function PlantillaModal({ plantillasExistentes, paginaActual, onClose, onGuardado }) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [guardando, setGuardando] = useState(false);

  const puedeGuardarDesdePagina = paginaActual && paginaActual.bloques.length > 0;

  const guardarComoPlantilla = async () => {
    if (!nombre.trim()) return alert('Ponle un nombre a la plantilla.');
    if (!puedeGuardarDesdePagina) return alert('La página activa no tiene bloques para plantillar.');
    setGuardando(true);
    try {
      const dto = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || null,
        columnas: paginaActual.columnas,
        filas: paginaActual.filas,
        bloques: paginaActual.bloques.map(b => ({
          columnaInicio: b.columnaInicio,
          filaInicio:    b.filaInicio,
          columnasSpan:  b.columnasSpan,
          filasSpan:     b.filasSpan,
          // El slot conserva el TIPO pero no el contenido concreto
          tipoSlot:      b.tipo,
          elementoData:  b.elementoData ?? null,
          etiqueta:      null,
        })),
      };
      await maquetaService.crearPlantilla(dto);
      onGuardado?.();
    } catch (err) {
      alert('Error guardando plantilla: ' + err.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarPlantilla = async (plantillaId) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await maquetaService.eliminarPlantilla(plantillaId);
      onGuardado?.();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999,
    }}
      onClick={onClose}
    >
      <div style={{
        background:'#fff', borderRadius:8, width:520, maxHeight:'80vh',
        display:'flex', flexDirection:'column', overflow:'hidden',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding:'12px 16px', background:'#1B4332', color:'#fff',
          display:'flex', alignItems:'center', gap:10,
        }}>
          <strong style={{ flex:1 }}>Plantillas de página</strong>
          <button onClick={onClose} style={{ background:'transparent', border:'none', color:'#fff', cursor:'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {/* Guardar página actual como plantilla */}
        <div style={{ padding:16, borderBottom:'1px solid #eee' }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#1B4332', marginBottom:8 }}>
            Guardar página actual como plantilla
          </div>
          {!puedeGuardarDesdePagina && (
            <div style={{ fontSize:12, color:'#c9a227', marginBottom:8 }}>
              La página activa no tiene bloques. Diseña algo primero.
            </div>
          )}
          <input
            type="text" placeholder="Nombre (ej. Portada nacional)"
            value={nombre} onChange={(e) => setNombre(e.target.value)}
            disabled={!puedeGuardarDesdePagina}
            style={{ width:'100%', padding:'8px 10px', border:'1px solid #ddd', borderRadius:4, fontSize:13, marginBottom:6 }}
          />
          <textarea
            placeholder="Descripción (opcional)"
            value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
            disabled={!puedeGuardarDesdePagina}
            rows={2}
            style={{ width:'100%', padding:'8px 10px', border:'1px solid #ddd', borderRadius:4, fontSize:13, marginBottom:8, resize:'vertical' }}
          />
          <button
            onClick={guardarComoPlantilla}
            disabled={guardando || !puedeGuardarDesdePagina || !nombre.trim()}
            style={{
              background:'#1B4332', color:'#fff', border:'none', borderRadius:4,
              padding:'6px 14px', fontSize:13, fontWeight:600, cursor:'pointer',
              display:'inline-flex', alignItems:'center', gap:6,
              opacity: (guardando || !puedeGuardarDesdePagina || !nombre.trim()) ? 0.5 : 1,
            }}
          >
            <Save size={14} /> {guardando ? 'Guardando…' : 'Guardar plantilla'}
          </button>
        </div>

        {/* Lista de existentes */}
        <div style={{ flex:1, overflowY:'auto', padding:16 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#1B4332', marginBottom:8 }}>
            Plantillas existentes ({plantillasExistentes.length})
          </div>
          {plantillasExistentes.length === 0 ? (
            <div style={{ color:'#888', fontSize:12, padding:12, textAlign:'center' }}>
              No hay plantillas todavía.
            </div>
          ) : plantillasExistentes.map(p => (
            <div key={p.id} style={{
              padding:10, border:'1px solid #eee', borderRadius:4, marginBottom:6,
              display:'flex', alignItems:'center', gap:10,
            }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600, fontSize:13 }}>{p.nombre}</div>
                {p.descripcion && <div style={{ fontSize:11, color:'#666' }}>{p.descripcion}</div>}
                <div style={{ fontSize:10, color:'#888' }}>
                  {p.columnas} col × {p.filas} filas · {p.bloques.length} bloques
                </div>
              </div>
              <button onClick={() => eliminarPlantilla(p.id)}
                style={{ background:'transparent', border:'1px solid #dc2626', color:'#dc2626', borderRadius:4, padding:'4px 8px', fontSize:11, cursor:'pointer' }}>
                Eliminar
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
